import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from "../modes.module.css";
import BackButton from '../common/BackButton/BackButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const ExamMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [examType, setExamType] = useState(null);
  const [story, setStory] = useState(null);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.exam || 1;
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [completingSession, setCompletingSession] = useState(false);
  
  // New state for story-based exam
  const [storyContent, setStoryContent] = useState([]);
  const [selectedSentence, setSelectedSentence] = useState(null);
  
  // New state for exam timer
  const [examTimeRemaining, setExamTimeRemaining] = useState(30 * 60); // 30 minutes in seconds
  const [examTimerActive, setExamTimerActive] = useState(false);

  const navigate = useNavigate();

  // Add effect to track question start time
  useEffect(() => {
    if (questions.length > 0) {
      setQuestionStartTime(Date.now());
      setSelectedSentence(null); // Reset selection when question changes
      
      // If it's a story exam, split the story content into sentences
      if (examType === 'story' && story) {
        const sentences = splitIntoSentences(story.content);
        setStoryContent(sentences);
      }
    }
  }, [currentQuestion, questions, examType, story]);

  // Add effect for exam timer
  useEffect(() => {
    let timerInterval;
    
    if (examTimerActive && examTimeRemaining > 0) {
      timerInterval = setInterval(() => {
        setExamTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerInterval);
            handleCompletion(); // Auto-submit when timer reaches zero
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [examTimerActive, examTimeRemaining]);

  // Format time remaining for display
  const formatTimeRemaining = () => {
    const minutes = Math.floor(examTimeRemaining / 60);
    const seconds = examTimeRemaining % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Function to split story into sentences
  const splitIntoSentences = (text) => {
    if (!text) return [];
    
    // Split by sentence endings (., !, ?) followed by a space or end of string
    // const sentenceRegex = /[^.!?]+[.!?]+\s*/g;
    const sentenceRegex = /[^.?!]+[.!?]+[\])'"`’”]*|.+/g;
    const sentences = text.match(sentenceRegex) || [];
    
    return sentences.map(sentence => sentence.trim());
  };

  const startExam = async (type) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        mode: type === 'story' ? 'story_exam' : 'exam',
        difficulty: difficulty
      });

      const data = await apiRequest(`/questions/generate?${params}`);

      // For story exam, the response includes both story and questions
      if (type === 'story') {
        setStory(data.story);
        setQuestions(data.questions);
        
        // Split the story content into sentences
        const sentences = splitIntoSentences(data.story.content);
        setStoryContent(sentences);
      } else {
        setQuestions(data);
      }

      // Start new session
      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: type === 'story' ? 'story_exam' : 'exam',
          difficulty: difficulty,
          questions: type === 'story' ? data.questions.map(q => q._id) : data.map(q => q._id)
        })
      });

      localStorage.setItem('currentSessionId', sessionData._id);
      setExamType(type);
      
      // Start the exam timer when exam begins
      setExamTimerActive(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle sentence selection for story-based exam
  const handleSentenceSelect = (index) => {
    if (isSubmitting || showExplanation) return;
    setSelectedSentence(index);
  };

  // Handle answer submission for story-based exam
  const handleSubmitStoryAnswer = async () => {
    if (isSubmitting || showExplanation || selectedSentence === null) return;
    
    setSubmittingAnswer(true);
    setIsSubmitting(true);

    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const currentQ = questions[currentQuestion];
      const sentence = storyContent[selectedSentence];
      
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      const result = await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer: sentence,
          timeSpent
        })
      });

      setAnswers(prev => ({ ...prev, [currentQuestion]: {
        sentence,
        index: selectedSentence,
        isCorrect: result.isCorrect
      }}));
      
      // In exam mode, we don't show explanations immediately
      // Just move to the next question
      if (currentQuestion + 1 >= questions.length) {
        handleCompletion();
      } else {
        setCurrentQuestion(prev => prev + 1);
        setSelectedSentence(null);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
      setSubmittingAnswer(false);
    }
  };

  // Original handle answer for multiple choice questions
  const handleAnswer = async (answer) => {
    if (isSubmitting) return;
    
    setSelectedAnswer(answer);
    setSubmittingAnswer(true);
    setIsSubmitting(true);

    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const currentQ = questions[currentQuestion];
      
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer,
          timeSpent
        })
      });

      setAnswers(prev => ({ ...prev, [currentQuestion]: answer }));
      
      // In exam mode, we don't show explanations immediately
      // Just move to the next question
      if (currentQuestion + 1 >= questions.length) {
        handleCompletion();
      } else {
        setCurrentQuestion(prev => prev + 1);
        setSelectedAnswer(null);
        setSubmittingAnswer(false);
      }
      
    } catch (err) {
      setError(err.message);
      setSubmittingAnswer(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    setShowExplanation(false);
    if (currentQuestion + 1 >= questions.length) {
      handleCompletion();
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleCompletion = async () => {
    setCompletingSession(true);
    
    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const results = await apiRequest(`/sessions/${sessionId}/end`, {
        method: 'POST'
      });

      localStorage.removeItem('currentSessionId');
      updateProgress(results);
      
      // Navigate to results page with session results and questions
      navigate('/results', { 
        state: { 
          sessionResults: results,
          questions: questions
        } 
      });
    } catch (err) {
      setError(err.message);
      setCompletingSession(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  // Show exam type selection if not yet selected
  if (!examType) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.titleExam}>Select Exam Type</h2>
          <div className={styles.examTypeGrid}>
            <button
              className={styles.examTypeButton}
              onClick={() => startExam('regular')}
            >
              <span className={styles.examIcon}>📝</span>
              <h3>Standard Exam</h3>
              <p>Complete a comprehensive grammar assessment</p>
            </button>
            <button
              className={styles.examTypeButton}
              onClick={() => startExam('story')}
            >
              <span className={styles.examIcon}>📚</span>
              <h3>Story-based Exam</h3>
              <p>Test your grammar through an engaging story</p>
            </button>
          </div>
          <BackButton />
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const currentAnswer = answers[currentQuestion];

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.titleExam}>
            {examType === 'story' ? 'Story-based Exam' : 'Grammar Exam'}
          </h2>
          {examTimerActive && (
              <span className={`${styles.examTimer} ${examTimeRemaining < 300 ? styles.timerWarning : ''}`}>
                Time: {formatTimeRemaining()}
              </span>
            )}
          <div className={styles.examInfo}>
            
            <span className={styles.questionCount}>
              Question {currentQuestion + 1} of {questions.length}
            </span>
          </div>
        </div>

        <div className={styles.progressContainer}>
          <div 
            className={styles.progressBar} 
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={styles.questionTextExam}>
          {currentQ?.content.text}
        </div>

        {/* Story content for story-based exam */}
        {examType === 'story' && story && (
          <div className={styles.storyContent}>
            {storyContent.map((sentence, index) => (
              <span 
                key={index}
                className={`${styles.sentenceSpan} 
                  ${selectedSentence === index ? styles.selectedSentence : ''}`}
                onClick={() => handleSentenceSelect(index)}
              >
                {sentence}{' '}
              </span>
            ))}
          </div>
        )}

        {/* Multiple choice options for regular exam */}
        {examType !== 'story' && (
          <div className={styles.optionsContainer}>
            {currentQ?.content.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                className={` ${styles.optionButton} ${styles.optionButtonExam} ${
                  submittingAnswer && selectedAnswer === option ? styles.selected : ''
                }`}
                disabled={isSubmitting}
              >
                {submittingAnswer && selectedAnswer === option ? (
                  <span className={styles.optionWithLoader}>
                    {option}
                    <span className={styles.miniLoader}></span>
                  </span>
                ) : (
                  option
                )}
              </button>
            ))}
          </div>
        )}

        {/* Check Answer button for story-based exam */}
        {examType === 'story' && (
          <div className={styles.actionContainer}>
            <button 
              className={`${styles.checkAnswerButton} ${selectedSentence === null ? styles.disabled : ''}`}
              onClick={handleSubmitStoryAnswer}
              disabled={selectedSentence === null || isSubmitting}
            >
              {submittingAnswer ? 'loading...' : 'Next'}
            </button>
          </div>
        )}

        <BackButton />
      </div>
    </div>
  );
};

export default ExamMode; 